/**
 * Carte « À réviser » (réutilisabilité — Étape 11, révision).
 * Compétences maîtrisées puis retombées sous le seuil (décroissance) → l'app
 * propose de les réviser. Couleur domaine « progression » (vert). Masquée si rien.
 *
 * ── Écrite le 12/07/2026, montée le 16/09 ────────────────────────────────────
 * Elle est restée deux mois sans être branchée nulle part, alors que tout le reste
 * de la chaîne tournait : la décroissance est calculée à chaque appel
 * (`competency-graph.ts` → `decayedScore`/`needsReview`) et `toReview` part au
 * navigateur depuis `api/competency-profile.ts`. Ce composant en est le SEUL
 * consommateur — sans lui, le serveur calculait ce que l'apprenant perdait et
 * personne ne le lui disait. Un test vérifie désormais qu'il reste monté.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { competencyApi, type CompetencyToReview } from "../lib/api";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { Button } from "./ui/button";

export function ReviewCard({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<CompetencyToReview[]>([]);

  useEffect(() => {
    competencyApi.getMine().then((r) => setItems(r.toReview ?? [])).catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border bg-white p-5 md:p-6 shadow-[var(--elev-2)]" style={{ borderColor: "color-mix(in srgb, #1E7A3C 25%, transparent)" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ backgroundImage: "var(--grad-progress)", boxShadow: "var(--elev-2)" }}>
          <RotateCcw className="h-5 w-5" />
        </span>
        <h2 className="text-lg font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          {t("dashboard.reviewTitle")}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t("dashboard.reviewHint")}
      </p>
      <ul className="space-y-2">
        {items.map((c) => (
          <li key={`${c.phaseKey}-${c.key}`} className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "var(--border)" }}>
            <div className="flex-1 min-w-0">
              {/* Libellé de critère : TOUJOURS via `pickScoringLabel` (règle CLAUDE.md) — une
                  lecture brute affiche du français en mode anglais dès qu'un champ manque. */}
              <div className="font-medium text-sm truncate" style={{ color: "#384872" }}>{pickScoringLabel(c, i18n.language)}</div>
              <div className="text-xs" style={{ color: "#926B00" }}>{c.score}/100</div>
            </div>
            {/* `sessions-training` a quitté la barre de navigation à la refonte de juillet :
                on renvoie vers l'écran d'entraînement actuel. */}
            <Button size="sm" variant="signature" onClick={() => onNavigate?.("m-entrainer")}>
              {t("dashboard.reviewCta")}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
